class HeaderScrollHandler {
  constructor(headerElement) {
    this.header = headerElement;
    this.lastScrollTop = 0;
    this.headerIsAlwaysSticky = this.header.dataset.alwaysSticky === 'true';

    this.announcementBar = document.querySelector('.section-announcement-bar');
    this.announcementBarIsSticky = this.announcementBar?.querySelector('.announcement-bar-new')?.dataset.sticky === 'true';
    this.announcementBarHeight = 0;

    this.SCROLL_THRESHOLD_TOP = 20;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setAnnouncementBarHeight();
    this.setHeaderHeight();
    this.setupPageLoadHandler();
  }

  setupEventListeners() {
    this.scrollHandler = this.throttle(() => {
      this.onScroll();
    }, 16);

    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    this.resizeHandler = this.debounce(() => {
      this.setAnnouncementBarHeight();
      this.setHeaderHeight();
    }, 250);

    window.addEventListener('resize', this.resizeHandler);
  }

  setAnnouncementBarHeight() {
    if (this.announcementBar && this.announcementBarIsSticky) {
      this.announcementBarHeight = this.announcementBar.offsetHeight;
      document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBarHeight}px`);
    }
  }

  setHeaderHeight() {
    if (this.header) {
      const headerHeight = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  }

  setupPageLoadHandler() {
    this.handlePageLoad = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollThreshold = this.SCROLL_THRESHOLD_TOP + (this.announcementBarHeight || 0);

      if (scrollTop <= scrollThreshold) {
        this.resetHeaderState();
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.handlePageLoad);
    } else {
      this.handlePageLoad();
    }

    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.handlePageLoad);
    }
  }

  onScroll() {
    if (this.predictiveSearch?.isOpen) return;

    requestAnimationFrame(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.handleHeaderScroll(scrollTop);
    });
  }

  handleHeaderScroll(scrollTop) {
    const combinedHeight = (this.announcementBarHeight || 0) + this.header.offsetHeight;
    const scrollThreshold = this.SCROLL_THRESHOLD_TOP + combinedHeight;
    if (scrollTop <= scrollThreshold) {
      this.resetHeaderState();
      this.lastScrollTop = scrollTop;
      return;
    }

    if (this.announcementBar && this.announcementBarIsSticky) {
      const currentAnnouncementBarHeight = this.announcementBar.offsetHeight;
      if (currentAnnouncementBarHeight !== this.announcementBarHeight) {
        this.announcementBarHeight = currentAnnouncementBarHeight;
        document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBarHeight}px`);
        this.setHeaderHeight();
      }
    }

    this.lastScrollTop = scrollTop;
  }

  resetHeaderState() {
    if (this.headerIsAlwaysSticky) return;

    this.header.classList.remove(
      'scrolled-past-header',
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate'
    );
  }

  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;

    return function (...args) {
      const currentTime = Date.now();

      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(
          () => {
            func.apply(this, args);
            lastExecTime = Date.now();
          },
          delay - (currentTime - lastExecTime)
        );
      }
    };
  }

  debounce(func, delay) {
    let timeoutId;

    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  destroy() {
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);

    if (this.handlePageLoad) {
      document.removeEventListener('DOMContentLoaded', this.handlePageLoad);
      window.removeEventListener('load', this.handlePageLoad);
    }
  }
}

window.HeaderScrollHandler = HeaderScrollHandler;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHeaderScrollHandler);
} else {
  initializeHeaderScrollHandler();
}

function initializeHeaderScrollHandler() {
  const stickyHeaderEl = document.querySelector('sticky-header');
  if (stickyHeaderEl) {
    const headerElement = stickyHeaderEl.header || document.querySelector('.section-header');
    if (headerElement) {
      stickyHeaderEl.headerScrollHandler = new HeaderScrollHandler(headerElement);
    }
    return;
  }

  const headerElement = document.querySelector('.section-header');
  if (headerElement) {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop <= 20) {
      headerElement.classList.remove(
        'scrolled-past-header',
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate'
      );
    }

    window.headerScrollHandler = new HeaderScrollHandler(headerElement);
  }
}
